import { useSelector } from "react-redux";
import { useMemo } from "react";
import { AdminPermissions } from "../SourceData";

const usePermissions = () => {
  const { userData } = useSelector((state) => state.auth);
  const userPermissions = userData?.user?.permissions?.map(Number) || [];
  const isAdmin = userData?.user?.role === "admin";

  console.log(userPermissions);

  // We use useMemo to cache the permissions mapping for better performance
  const permissionsMap = useMemo(() => {
    // Create a mapping of section IDs to their sub-permissions
    const sectionToSubPerms = {};
    const permToSection = {};

    AdminPermissions.forEach((section) => {
      // Store all sub-permissions for this section
      sectionToSubPerms[section.id] = section.sub_permissions.map(
        (subPerm) => subPerm.id
      );

      // For each sub-permission, store which section it belongs to
      section.sub_permissions.forEach((subPerm) => {
        permToSection[subPerm.id] = section.id;
      });
    });

    return { sectionToSubPerms, permToSection };
  }, []);

  const hasPermission = (permissionId) => {
    // Admin users have all permissions
    if (isAdmin) {
      return true;
    }

    // First check if user directly has this permission
    if (!userPermissions.includes(permissionId)) {
      return false;
    }

    // If this is a section's view permission (first sub-permission), we're done
    const { permToSection } = permissionsMap;
    const sectionId = permToSection[permissionId];

    // If this permission is a section ID itself, or we can't find its section,
    // just check if it exists in the user permissions
    if (permissionId === sectionId || !sectionId) {
      return userPermissions.includes(permissionId);
    }

    // For other sub-permissions, also verify the user has the section view permission
    // (which has the same ID as the section itself)
    return (
      userPermissions.includes(permissionId) &&
      userPermissions.includes(sectionId)
    );
  };

  const hasAnyPermission = (permissionIds) => {
    // Admin users have all permissions
    if (isAdmin) {
      return true;
    }

    return permissionIds.some((id) => hasPermission(id));
  };

  const hasAllPermissions = (permissionIds) => {
    // Admin users have all permissions
    if (isAdmin) {
      return true;
    }

    return permissionIds.every((id) => hasPermission(id));
  };

  const getSectionPermissions = (sectionId) => {
    const { sectionToSubPerms } = permissionsMap;
    return sectionToSubPerms[sectionId] || [];
  };

  const canViewSection = (sectionId) => {
    // Admin users have all permissions
    if (isAdmin) {
      return true;
    }

    // Check if user has the section view permission (same ID as section)
    return userPermissions.includes(sectionId);
  };

  const getAccessibleSections = () => {
    // Admin users have access to all sections
    if (isAdmin) {
      return AdminPermissions.map((section) => section.id);
    }

    // For regular users, find sections where they have the view permission
    return AdminPermissions.filter((section) =>
      userPermissions.includes(section.id)
    ).map((section) => section.id);
  };

  return {
    userPermissions,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getSectionPermissions,
    canViewSection,
    getAccessibleSections,
  };
};

export default usePermissions;
